import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import NavBar from '../components/NavBar';
import { useLanguage } from '../i18n/LanguageContext';
import LanguageSwitcher from '../components/LanguageSwitcher';

const Resources = ({ user, onLogout }) => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('local');
  const [userLocations, setUserLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [localResources, setLocalResources] = useState(null);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingResources, setLoadingResources] = useState(false);

  // Fetch user locations when logged in
  useEffect(() => {
    if (user) {
      fetchUserLocations();
    }
  }, [user]);

  // Fetch local resources when location is selected
  useEffect(() => {
    if (selectedLocation) {
      fetchLocalResources(selectedLocation.county, selectedLocation.state);
    }
  }, [selectedLocation]);

  const fetchUserLocations = async () => {
    setLoadingLocations(true);
    try {
      const response = await axios.get(`${API}/resources/user-locations`);
      const locations = response.data.locations || [];
      setUserLocations(locations);
      
      // Auto-select first location if available
      if (locations.length > 0) {
        setSelectedLocation(locations[0]);
      }
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    } finally {
      setLoadingLocations(false);
    }
  };

  const fetchLocalResources = async (county, state) => {
    setLoadingResources(true);
    try {
      const response = await axios.post(`${API}/resources/local`, { county, state });
      setLocalResources(response.data);
    } catch (error) {
      console.error('Failed to fetch local resources:', error);
      toast.error('Failed to load local resources');
    } finally {
      setLoadingResources(false);
    }
  };

  // National Resources (always available)
  const nationalResources = [
    {
      title: "Alzheimer's & Dementia Organizations",
      icon: "🧠",
      resources: [
        { name: "Alzheimer's Association", url: "https://www.alz.org", phone: "800-272-3900", description: "24/7 helpline, support groups, education, and care resources nationwide" },
        { name: "Alzheimer's Foundation of America", url: "https://alzfdn.org", phone: "866-232-8484", description: "Free memory screenings, educational programs, and caregiver support" },
        { name: "Dementia Action Alliance", url: "https://daanow.org", description: "Resources for living well with dementia and caregiver support" },
        { name: "National Institute on Aging", url: "https://www.nia.nih.gov/health/alzheimers", description: "Research-based information about Alzheimer's disease" },
        { name: "Lewy Body Dementia Association", url: "https://www.lbda.org", phone: "800-539-9767", description: "Support for Lewy Body Dementia patients and caregivers" }
      ]
    },
    {
      title: "National Caregiver Support",
      icon: "🤝",
      resources: [
        { name: "Family Caregiver Alliance", url: "https://www.caregiver.org", phone: "800-445-8106", description: "Education, services, research, and advocacy for caregivers" },
        { name: "AARP Caregiving Resource Center", url: "https://www.aarp.org/caregiving", description: "Tools, tips, and resources for family caregivers" },
        { name: "Caregiver Action Network", url: "https://www.caregiveraction.org", phone: "855-227-3640", description: "Free education and peer support for family caregivers" },
        { name: "National Alliance for Caregiving", url: "https://www.caregiving.org", description: "Research and advocacy to improve quality of life for caregivers" },
        { name: "Well Spouse Association", url: "https://wellspouse.org", phone: "732-577-8899", description: "Support for spousal caregivers" }
      ]
    },
    {
      title: "Federal Government Resources",
      icon: "🏛️",
      resources: [
        { name: "Eldercare Locator", url: "https://eldercare.acl.gov", phone: "800-677-1116", description: "Connect to local services for older adults and caregivers" },
        { name: "Medicare.gov", url: "https://www.medicare.gov", phone: "800-633-4227", description: "Official Medicare information and coverage details" },
        { name: "Medicaid.gov", url: "https://www.medicaid.gov", description: "Health coverage for eligible low-income adults and seniors" },
        { name: "Benefits.gov", url: "https://www.benefits.gov", description: "Find government benefits you may be eligible for" },
        { name: "VA Caregiver Support", url: "https://www.caregiver.va.gov", phone: "855-260-3274", description: "Support for caregivers of veterans" }
      ]
    },
    {
      title: "Respite Care & Home Services",
      icon: "🏠",
      resources: [
        { name: "ARCH National Respite Network", url: "https://archrespite.org", phone: "703-256-2084", description: "Find respite care services in your area" },
        { name: "National Adult Day Services Association", url: "https://www.nadsa.org", description: "Locate adult day care centers nationwide" },
        { name: "Home Instead Senior Care", url: "https://www.homeinstead.com", description: "In-home care services for seniors nationwide" },
        { name: "Care.com", url: "https://www.care.com", description: "Find local caregivers and senior care providers" }
      ]
    },
    {
      title: "Mental Health & Self-Care",
      icon: "💚",
      resources: [
        { name: "988 Suicide & Crisis Lifeline", url: "https://988lifeline.org", phone: "988", description: "Call or text 988 for free crisis support 24/7" },
        { name: "NAMI (National Alliance on Mental Illness)", url: "https://www.nami.org", phone: "800-950-6264", description: "Mental health support and resources" },
        { name: "Crisis Text Line", url: "https://www.crisistextline.org", description: "Text HOME to 741741 for free crisis support" },
        { name: "Caregiver Burnout Prevention (Mayo Clinic)", url: "https://www.mayoclinic.org/healthy-lifestyle/stress-management/in-depth/caregiver-stress/art-20044784", description: "Guide to managing caregiver stress" }
      ]
    },
    {
      title: "Financial & Legal Resources",
      icon: "📋",
      resources: [
        { name: "National Academy of Elder Law Attorneys", url: "https://www.naela.org", description: "Find elder law attorneys for legal planning" },
        { name: "LongTermCare.gov", url: "https://longtermcare.acl.gov", description: "Information about paying for long-term care" },
        { name: "Social Security Administration", url: "https://www.ssa.gov", phone: "800-772-1213", description: "Social Security benefits and disability programs" },
        { name: "BenefitsCheckUp (NCOA)", url: "https://www.benefitscheckup.org", description: "Find benefits programs for seniors" }
      ]
    }
  ];

  const renderResourceCard = (resource, index) => (
    <div
      key={index}
      style={{
        padding: '1.25rem',
        background: '#f8f9fa',
        borderRadius: '10px',
        border: '1px solid #e8f4f8'
      }}
    >
      <a
        href={resource.url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        <h4 style={{ margin: '0 0 0.5rem 0', color: '#2c5364', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {resource.name}
          <span style={{ fontSize: '0.8rem', color: '#6b8899' }}>↗</span>
        </h4>
      </a>
      <p style={{ margin: '0 0 0.75rem 0', color: '#6b8899', fontSize: '0.9rem', lineHeight: '1.4' }}>
        {resource.description}
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
        {resource.phone && (
          <a 
            href={`tel:${resource.phone.replace(/[^0-9]/g, '')}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.25rem',
              padding: '0.35rem 0.75rem',
              background: '#e7f3ff',
              color: '#0c5460',
              borderRadius: '20px',
              fontSize: '0.85rem',
              textDecoration: 'none',
              fontWeight: '500'
            }}
          >
            📞 {resource.phone}
          </a>
        )}
        {resource.search_link && (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.25rem',
            padding: '0.35rem 0.75rem',
            background: '#fff3cd',
            color: '#856404',
            borderRadius: '20px',
            fontSize: '0.85rem',
            fontWeight: '500'
          }}>
            🔍 Search Link
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="dashboard-container" data-testid="resources-page">
      {user ? (
        <NavBar onLogout={onLogout} />
      ) : (
        <nav className="dashboard-nav">
          <Link to="/resources">
            <img src="/careguard-logo.png" alt="CareGuard" className="nav-logo" style={{ cursor: 'pointer' }} />
          </Link>
          <div className="nav-links">
            <Link to="/resources" className={`nav-link ${location.pathname === '/resources' ? 'active' : ''}`}>
              Resources
            </Link>
            <Link to="/auth" className="nav-link">Login</Link>
            <LanguageSwitcher />
          </div>
        </nav>
      )}

      <div className="dashboard-content page-container">
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 className="page-title">Caregiver Tools & Resources</h1>
          <p style={{ color: '#6b8899', maxWidth: '700px', margin: '0 auto', fontSize: '1.1rem' }}>
            {user && userLocations.length > 0 
              ? 'Personalized resources based on your saved locations'
              : 'A curated collection of helpful resources for caregivers'
            }
          </p>
        </div>

        {/* Quick Access Emergency Banner */}
        <div style={{ 
          background: 'linear-gradient(135deg, #d9534f 0%, #c9302c 100%)', 
          color: 'white', 
          padding: '1rem 1.5rem', 
          borderRadius: '12px',
          marginBottom: '2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div>
            <strong style={{ fontSize: '1.1rem' }}>🚨 Emergency?</strong>
            <span style={{ marginLeft: '0.5rem' }}>Call 911 for immediate help</span>
          </div>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <a href="tel:911" style={{ background: 'white', color: '#d9534f', padding: '0.5rem 1rem', borderRadius: '6px', textDecoration: 'none', fontWeight: '600' }}>
              📞 Call 911
            </a>
            <a href="tel:988" style={{ background: 'rgba(255,255,255,0.2)', color: 'white', padding: '0.5rem 1rem', borderRadius: '6px', textDecoration: 'none', fontWeight: '600' }}>
              💚 Crisis Line: 988
            </a>
            <a href="tel:800-272-3900" style={{ background: 'rgba(255,255,255,0.2)', color: 'white', padding: '0.5rem 1rem', borderRadius: '6px', textDecoration: 'none', fontWeight: '600' }}>
              🧠 Alzheimer's 24/7: 800-272-3900
            </a>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ 
          display: 'flex', 
          gap: '0.5rem', 
          marginBottom: '2rem',
          background: '#f0f4f8',
          padding: '0.5rem',
          borderRadius: '12px',
          flexWrap: 'wrap'
        }}>
          {user && userLocations.length > 0 && (
            <button
              onClick={() => setActiveTab('local')}
              style={{
                flex: 1,
                minWidth: '150px',
                padding: '1rem 1.5rem',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '1rem',
                background: activeTab === 'local' ? 'linear-gradient(135deg, #2c5364 0%, #203a43 100%)' : 'transparent',
                color: activeTab === 'local' ? 'white' : '#2c5364',
                transition: 'all 0.2s ease'
              }}
              data-testid="tab-local"
            >
              📍 My Local Resources
            </button>
          )}
          <button
            onClick={() => setActiveTab('national')}
            style={{
              flex: 1,
              minWidth: '150px',
              padding: '1rem 1.5rem',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '1rem',
              background: activeTab === 'national' ? 'linear-gradient(135deg, #2c5364 0%, #203a43 100%)' : 'transparent',
              color: activeTab === 'national' ? 'white' : '#2c5364',
              transition: 'all 0.2s ease'
            }}
            data-testid="tab-national"
          >
            🇺🇸 National Resources
          </button>
        </div>

        {/* Local Resources Tab */}
        {activeTab === 'local' && user && (
          <>
            {/* Location Selector */}
            {userLocations.length > 0 ? (
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#2c5364', fontWeight: '600' }}>
                  Select Location:
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {userLocations.map((loc, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedLocation(loc)}
                      style={{
                        padding: '0.75rem 1.25rem',
                        border: selectedLocation === loc ? '2px solid #2c5364' : '2px solid #e8f4f8',
                        borderRadius: '8px',
                        background: selectedLocation === loc ? '#e7f3ff' : 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start'
                      }}
                    >
                      <span style={{ fontWeight: '600', color: '#2c5364' }}>
                        {loc.type === 'caregiver' ? '👤' : '👥'} {loc.name}
                      </span>
                      <span style={{ fontSize: '0.85rem', color: '#6b8899' }}>
                        {loc.county}, {loc.state}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="section-card" style={{ marginBottom: '2rem', textAlign: 'center', padding: '2rem' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📍</div>
                <h3 style={{ color: '#2c5364', marginBottom: '0.5rem' }}>No Locations Set</h3>
                <p style={{ color: '#6b8899', marginBottom: '1rem' }}>
                  Add your county and state in your profile to see personalized local resources.
                </p>
                <Link to="/profile/edit" className="submit-button" style={{ display: 'inline-block', width: 'auto', padding: '0.75rem 1.5rem', textDecoration: 'none' }}>
                  Go to Profile Settings
                </Link>
              </div>
            )}

            {/* Local Resources Display */}
            {loadingResources ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <p>Loading local resources...</p>
              </div>
            ) : localResources && selectedLocation ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                {/* Section Header */}
                <div style={{ 
                  background: 'linear-gradient(135deg, #e7f3ff 0%, #d1ecf1 100%)',
                  padding: '1rem 1.5rem',
                  borderRadius: '12px',
                  borderLeft: '4px solid #2c5364'
                }}>
                  <h2 style={{ margin: 0, color: '#2c5364', fontSize: '1.3rem' }}>
                    📍 Resources for {selectedLocation.county}, {selectedLocation.state}
                  </h2>
                  <p style={{ margin: '0.5rem 0 0', color: '#0c5460', fontSize: '0.95rem' }}>
                    Local agencies and services in your area
                  </p>
                </div>

                {localResources.categories?.map((category, index) => (
                  <div key={index} className="section-card">
                    <h2 className="section-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '1.5rem' }}>{category.icon}</span>
                      {category.title}
                    </h2>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
                      {category.resources.map((resource, rIndex) => renderResourceCard(resource, rIndex))}
                    </div>
                  </div>
                ))}

                <div style={{ padding: '1rem', background: '#fff3cd', borderRadius: '8px', borderLeft: '4px solid #ffc107' }}>
                  <p style={{ margin: 0, color: '#856404', fontSize: '0.9rem' }}>
                    <strong>💡 Tip:</strong> Click on the search links above to find specific services in {selectedLocation.county}. 
                    For the most accurate information, contact your local Area Agency on Aging at <a href="tel:800-677-1116" style={{ color: '#856404' }}>800-677-1116</a> (Eldercare Locator).
                  </p>
                </div>
              </div>
            ) : null}
          </>
        )}

        {/* National Resources Tab */}
        {activeTab === 'national' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            {/* Section Header */}
            <div style={{ 
              background: 'linear-gradient(135deg, #e7f3ff 0%, #d1ecf1 100%)',
              padding: '1rem 1.5rem',
              borderRadius: '12px',
              borderLeft: '4px solid #2c5364'
            }}>
              <h2 style={{ margin: 0, color: '#2c5364', fontSize: '1.3rem' }}>
                🇺🇸 National Resources
              </h2>
              <p style={{ margin: '0.5rem 0 0', color: '#0c5460', fontSize: '0.95rem' }}>
                Federal programs and nationwide organizations
              </p>
            </div>

            {nationalResources.map((category, index) => (
              <div key={index} className="section-card">
                <h2 className="section-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>{category.icon}</span>
                  {category.title}
                </h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
                  {category.resources.map((resource, rIndex) => renderResourceCard(resource, rIndex))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No user logged in - show prompt */}
        {!user && activeTab === 'local' && (
          <div className="section-card" style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔐</div>
            <h3 style={{ color: '#2c5364', marginBottom: '0.5rem' }}>Login for Personalized Resources</h3>
            <p style={{ color: '#6b8899', marginBottom: '1rem' }}>
              Sign in to see resources customized for your location and your clients' locations.
            </p>
            <Link to="/auth" className="submit-button" style={{ display: 'inline-block', width: 'auto', padding: '0.75rem 1.5rem', textDecoration: 'none' }}>
              Login / Register
            </Link>
          </div>
        )}

        {/* Disclaimer */}
        <div style={{ 
          marginTop: '2rem', 
          padding: '1rem 1.5rem', 
          background: '#f8f9fa', 
          borderRadius: '8px',
          borderLeft: '4px solid #6b8899'
        }}>
          <p style={{ margin: 0, color: '#6b8899', fontSize: '0.9rem' }}>
            <strong>Disclaimer:</strong> CareGuard provides these resources for informational purposes only. 
            We are not affiliated with any of these organizations. Please verify information directly with 
            each organization. In case of emergency, always call 911.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Resources;
